import { useMemo, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import StaffAggregatePresenceChart from "./StaffAggregatePresenceChart";
import StudentAggregatePresenceChart from "./StudentAggregatePresenceChart";

interface ChartProps {
  filterParams?: {
    location_id?: string;
    camera_id?: string;
    from?: string;
    to?: string;
    timezone?: string;
  };
  distributionType?: string;
  apisEnabled?: boolean;
}

interface Metadata {
  key: "staff" | "student";
  title: string;
  description: string;
  icon: React.ReactNode;
  component: React.FC<ChartProps>;
}

const metadata: Record<"staff" | "student", Metadata> = {
  staff: {
    key: "staff",
    title: "Aggregate Presence Across All Cameras",
    description: "aggregate presence",
    icon: (
      <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    component: StaffAggregatePresenceChart,
    // donehere
  },
  student: {
    key: "student",
    title: "Aggregate Presence Across All Cameras",
    description: "aggregate presence",
    icon: (
      <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    component: StudentAggregatePresenceChart,
  },
};

export interface FilterParams {
  location_id?: string;
  camera_id?: string;
  from?: string;
  to?: string;
  timezone?: string;
}

export interface PresenceChartWithToggleProps {
  filterParams?: FilterParams;
  /** When set, chart matches this role and the Student/Staff header toggles are hidden. */
  roleFilter?: "student" | "staff";
  /** Selected distribution type from API dropdown (e.g. student, staff, person). */
  distributionType?: string;
  /** When false, aggregate-presence distribution APIs are not called. */
  apisEnabled?: boolean;
}

export const PresenceChartWithToggle = ({
  filterParams,
  roleFilter,
  distributionType,
  apisEnabled = true,
}: PresenceChartWithToggleProps) => {
  const [internalChart, setInternalChart] = useState<"staff" | "student">("student");
  const isControlled = roleFilter != null;
  const selectedChart = isControlled ? roleFilter : internalChart;
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const currentMetadata = useMemo(() => metadata[selectedChart], [selectedChart]);
  const ChartComponent = currentMetadata.component;

  return (
    <div
      className={`p-3 sm:p-4 lg:p-6 rounded-2xl border ${
        isDarkMode
          ? "bg-[#1C202F]/55 border-slate-700/70"
          : "bg-white/95 border-gray-200"
      }`}
    >
      <Header
        metadata={currentMetadata}
        setSelectedChart={setInternalChart}
        selectedChart={selectedChart}
        showRoleToggle={!isControlled}
      />
      <div className="mt-3 sm:mt-4">
        <ChartComponent
          filterParams={filterParams}
          distributionType={distributionType}
          apisEnabled={apisEnabled}
        />
      </div>
    </div>
  );
};

interface HeaderProps {
  metadata: Metadata;
  setSelectedChart: (chart: "staff" | "student") => void;
  selectedChart: "staff" | "student";
  showRoleToggle: boolean;
}

const Header: React.FC<HeaderProps> = ({
  metadata,
  setSelectedChart,
  selectedChart,
  showRoleToggle,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const { title, description, icon } = metadata;

  return (
    <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start sm:items-center gap-2.5 sm:gap-3 min-w-0">
        <div
          className={`p-2 rounded-xl border shrink-0 ${
            isDarkMode
              ? "bg-teal-500/15 border-teal-500/25"
              : "bg-teal-500/10 border-teal-500/20"
          }`}
        >
          {icon}
        </div>
        <div>
          <h3
            className={`text-base sm:text-lg lg:text-xl font-bold tracking-tight ${
              isDarkMode ? "text-slate-50" : "text-slate-900"
            }`}
          >
            {title}
          </h3>
          <p
            className={`text-xs sm:text-sm ${
              isDarkMode ? "text-slate-400" : "text-slate-600"
            }`}
          >
            {description}
          </p>
        </div>
      </div>
      {showRoleToggle && (
        <div className="flex w-full sm:w-auto items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setSelectedChart("student")}
            className={`flex-1 sm:flex-none px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 shadow-sm
            ${
              selectedChart === "student"
                ? isDarkMode
                  ? "bg-teal-500/20 text-teal-100 ring-2 ring-teal-500/35 border border-teal-500/30"
                  : "bg-teal-600/15 text-teal-700 ring-2 ring-teal-600/25 border border-teal-600/25"
                : isDarkMode
                ? "bg-slate-800/40 text-slate-300 hover:bg-slate-800/55 border border-slate-700 hover:border-slate-600"
                : "bg-slate-50 text-slate-700 hover:bg-slate-100 border border-gray-200 hover:border-gray-300"
            }`}
          >
            Student
          </button>
          <button
            type="button"
            onClick={() => setSelectedChart("staff")}
            className={`flex-1 sm:flex-none px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 shadow-sm
            ${
              selectedChart === "staff"
                ? isDarkMode
                  ? "bg-teal-500/20 text-teal-100 ring-2 ring-teal-500/35 border border-teal-500/30"
                  : "bg-teal-600/15 text-teal-700 ring-2 ring-teal-600/25 border border-teal-600/25"
                : isDarkMode
                ? "bg-slate-800/40 text-slate-300 hover:bg-slate-800/55 border border-slate-700 hover:border-slate-600"
                : "bg-slate-50 text-slate-700 hover:bg-slate-100 border border-gray-200 hover:border-gray-300"
            }`}
          >
            Staff
          </button>
        </div>
      )}
    </div>
  );
};

export default PresenceChartWithToggle;
